import { FC, useState } from "react";
import { appointmentDetails } from "../../../../types/calendar";
import { Button, Modal, Select, message } from "antd";
import { useIntl } from "react-intl";
import ReservationReceipt from "../../../sales/ReservationReceipt";
import { MdDeleteForever, MdPrint } from "react-icons/md";

import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "../../../../services/react-query/queryClient";
import { handlePrint } from "../../../../utils/HandlePrint";
import {
  adminCancelAppointment,
  adminGetCancelReasons,
} from "../../../../network/admin";
import {
  cancelAppointment,
  getCancelReasons,
} from "../../../../network/calendar";

interface AppointmentModalFooterProps {
  appointment: appointmentDetails;
  closeModal: () => void;
  isAdmin?: boolean;
  canEdit: boolean;
}

const AppointmentModalFooter: FC<AppointmentModalFooterProps> = ({
  appointment,
  closeModal,
  canEdit,
  isAdmin,
}) => {
  const intl = useIntl();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [cancelReason, setCancelReason] = useState<string | null>(null);

  const { data: cancelReasonsData } = useQuery(
    ["cancelReasons", appointment?.location?.salon_id],
    () => {
      if (canEdit) {
        if (isAdmin) {
          return adminGetCancelReasons(appointment?.location?.salon_id);
        } else {
          return getCancelReasons();
        }
      }
    },
    {
      select: (res) => res?.data?.data,
    }
  );

  const { mutate, isLoading: isUpdating } = useMutation(
    () => {
      if (canEdit) {
        if (isAdmin) {
          return adminCancelAppointment(
            appointment?.location_id,
            appointment?.id,
            cancelReason
          );
        } else {
          return cancelAppointment(
            appointment?.location_id,
            appointment?.id,
            cancelReason
          );
        }
      }
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          queryClient.invalidateQueries(["appointment", appointment?.id]);
          queryClient.invalidateQueries(["appointments"]);
          setOpenDeleteModal(false);
        } else {
          message.error(
            res?.response?.data?.message ||
              intl.formatMessage({
                id: "somethingWentWrong",
                defaultMessage: "حدث خطأ",
              })
          );
        }
      },
    }
  );

  const handleDeleteAppointment = () => {
    mutate();
  };

  const options = Array.isArray(cancelReasonsData)
    ? cancelReasonsData?.map(
        (reason: { name: string; id: number }, i: number) => ({
          label: reason.name,
          value: reason.id,
        })
      )
    : [];

  return (
    <div className="space-y-2">
      <Button
        type="primary"
        block
        htmlType="button"
        className="flex gap-1 justify-center items-center"
        onClick={() => handlePrint(<ReservationReceipt data={appointment} />)}
        icon={<MdPrint />}
      >
        {/* <span>
          <LuPrinter size={20} />
        </span> */}
        <span>
          {intl.formatMessage({
            id: "printInvoice",
            defaultMessage: "طباعة الفاتورة",
          })}
        </span>
      </Button>
      {canEdit &&
      appointment?.status !== "canceled" &&
      appointment?.status !== "completed" ? (
        <Button
          type="text"
          block
          htmlType="button"
          className="flex gap-1 justify-center items-center"
          onClick={() => setOpenDeleteModal(true)}
        >
          <span>
            <MdDeleteForever color="red" />
          </span>
          <span className="text-red-600 font-semibold">
            {intl.formatMessage({
              id: "cancelReservation",
              defaultMessage: "الغاء الحجز",
            })}
          </span>
        </Button>
      ) : null}
      <Modal
        open={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4 pace-y-4 py-4">
          <div className="space-y-4">
            <h2 className="text-center text-lg font-semibold">
              {intl.formatMessage({
                id: "sureCancelAppointment",
                defaultMessage: " هل انت متأكد من الغاء الحجز؟ ",
              })}
            </h2>
            <p className="text-center text-color-sec-title">
              {intl.formatMessage({
                id: "cancelAppointmentAlertMsg",
                defaultMessage:
                  "سيتم حذف الغاء الحجز و لن تتمكن من التراجع عن الغاءه..",
              })}
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor="cancelReason">
              {intl.formatMessage({
                id: "cancelReason",
                defaultMessage: "سبب الغاء الحجز",
              })}
              <span className="text-color-sec-title">
                {intl.formatMessage({
                  id: "optional",
                  defaultMessage: "(اختياري)",
                })}
              </span>
            </label>

            <div>
              <Select
                className="w-full"
                options={options}
                onChange={(e) => setCancelReason(e)}
              />
            </div>
          </div>
          <div className="flex gap-2 ">
            <Button
              type="default"
              block
              htmlType="button"
              style={{
                backgroundColor: "red",
              }}
              className="bg-red-500 text-white"
              onClick={handleDeleteAppointment}
            >
              {intl.formatMessage({
                id: "yesCancelReservation",
                defaultMessage: "نعم، الغاء الحجز",
              })}
            </Button>
            <Button
              type="default"
              block
              htmlType="button"
              onClick={() => setOpenDeleteModal(false)}
            >
              {intl.formatMessage({ id: "cancel", defaultMessage: "الغاء" })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppointmentModalFooter;
